import { EditorState, convertToRaw, Modifier, Entity } from 'draft-js';
import invariant from 'invariant';
import { OrderedSet } from 'immutable';
import { convertFromHTML } from './draft-convert-html';
import { findBlockQuote } from './finder/findBlockQuote';
import { findCodeBlock } from './finder/findCodeBlock';
import { findImageAtomicBlock } from './finder/findImageAtomicBlock';
import { findTableBlock } from './finder/findTableBlock';
import { findImageEntity } from './finder/findImageEntity';
import { findLinkEntity } from './finder/findLinkEntity';
import { findHrBlock } from './finder/findHrBlock';
import { findHrEntity } from './finder/findHrEntity';

const handleMiddleware = (middleware, next) => middleware ? middleware(next) : next

/**
 * @param {HTMLParseOptions} options
 */
const lowConvert = (options) => convertFromHTML({
	htmlToStyle: handleMiddleware(
		options.htmlToStyle,
		/**
		 * 碰到特殊的节点，处理最后生成的格式
		 * @param {string} nodeName
		 * @param {HTMLElement} node
		 * @param {Draft.DraftInlineStyle} currentStyle
		 */
		(nodeName, node, currentStyle, inBlock) => {
			// 代码块里面样式清空
			if (inBlock === 'code-block') {
				return OrderedSet();
			}
			// 加粗
			if (nodeName === 'strong' || nodeName === 'b') {
				return currentStyle.add('BOLD');
			}
			// 已经包含高亮处理的代码块
			if (nodeName === 'code' && node.classList.contains('hljs')) {
				return currentStyle.remove('CODE');
			}
			return currentStyle;
		},
	),

	htmlToBlock: handleMiddleware(
		options.htmlToBlock,
		/**
		 * 特殊的节点，转换为指定类型的 block
		 * @param {string} nodeName
		 * @param {HTMLElement} node
		 * @param {'ul' | 'ol'} lastList
		 * @param {Draft.DraftBlockType} inBlock 当前节点处于已解析的块类型
		 */
		(nodeName, node, lastList, inBlock) => {
			if (inBlock === 'table') {
				return null;
			}
			return (
				findBlockQuote(nodeName, node) ||
				findCodeBlock(nodeName, node) ||
				findHrBlock(nodeName, node) ||
				findImageAtomicBlock(nodeName, node) ||
				findTableBlock(nodeName, node)
			);
		},
	),

	htmlToEntity: handleMiddleware(
		options.htmlToEntity,
		/**
		 * 特殊的节点添加实体
		 * @param {string} nodeName
		 * @param {HTMLElement} node
		 * @param {Function} createEntity DraftJS 提供的 createEntity 方法
		 */
		(nodeName, node, createEntity) => {
			return (
				findHrEntity(nodeName, node, createEntity) ||
				findImageEntity(nodeName, node, createEntity) ||
				findLinkEntity(nodeName, node, createEntity)
			);
		},
	),

	/**
	 * 查找文本中需要转换为实体的内容
	 * @param {string} text
	 * @param {Function} createEntity DraftJS 提供的 createEntity 方法
	 */
	textToEntity(text, createEntity) {
		// 暂无需求
		return [];
	},
});

export const convertHtmlToDraftContent = (html, options = {}) => {
	// 浏览器环境
	if (typeof window === 'object' && window.document) {
		return lowConvert(options)(html);
	}
	// Node 环境
	const node = options.node;
	invariant(
		typeof node === 'object'
			&& typeof node.global === 'object'
			&& typeof node.require === 'function',
		'在 Node 环境转换 HTML 内容到其它格式，请传入 options.node = { global, require }'
	);
	const { global, require } = node;
	// Node 环境
	let jsdom;
	try {
		// 不能直接 require，否则会被 webpack 打包
		jsdom = require('jsdom');
	} catch (err) {
		console.log(err);
	}
	if (!jsdom) {
		throw new Error('要转换 HTML 内容到其它格式，请安装 jsdom 模块');
	}
	const { JSDOM } = jsdom;
	const dom = new JSDOM(html);
	const { document, HTMLElement } = dom.window;
	const restore = replaceContext(global, { document, HTMLElement });
	const result = lowConvert(options)(html, undefined, () => document.body);
	restore();
	return result;
}

function replaceContext(context, values) {
	const origins = {};
	for (let [key, value] of Object.entries(values)) {
		origins[key] = context[key];
		context[key] = value;
	}
	return (() => {
		for (let [key, value] of Object.entries(values)) {
			context[key] = origins[key];
		}
	});
}



// WEBPACK FOOTER //
// ./lib/convert/html/convertHtmlToDraftContent.js